import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAppStore } from '@/store/appStore'
import { OFFLINE_SESSION } from '@/utils/offlineSessionConfig'
import { clearAllContentBundles, clearMediaCache } from '@/db/viewerDb'
import { fetchCurrentUser } from '@/api/currentUserApi'
import { trackEvent } from '@/services/appInsights'

const WARNING_TOAST_ID = 'offline-session-warning'
const OFFLINE_DISABLED_TOAST_ID = 'offline-disabled-info'

export function useOfflineSessionTimer() {
  const { t } = useTranslation()
  const {
    isOffline,
    isTrulyOffline,
    isManualOffline,
    offlineSessionStart,
    offlineSessionExpired,
    offlineSessionId,
    setOfflineSessionStart,
    setOfflineSessionExpired,
    setOfflineSessionId,
    setManualOffline,
  } = useAppStore()

  const wasOfflineRef = useRef(isOffline)
  const warningShownRef = useRef(false)
  const recoveryInProgressRef = useRef(false)

  // Effect 1 — track offline transitions to start/clear the timer
  useEffect(() => {
    const wasOffline = wasOfflineRef.current
    wasOfflineRef.current = isOffline

    if (!wasOffline && isOffline) {
      if (offlineSessionStart === null && !offlineSessionExpired) {
        const sessionId = crypto.randomUUID()
        setOfflineSessionId(sessionId)
        setOfflineSessionStart(Date.now())
        warningShownRef.current = false
        const trigger = isManualOffline && !isTrulyOffline ? 'manual' : 'connectivity_lost'
        trackEvent('Offline Session Started', { offlineSessionId: sessionId, trigger })
      }
    } else if (wasOffline && !isOffline) {
      if (!offlineSessionExpired) {
        const sessionId = offlineSessionId
        setOfflineSessionStart(null)
        setOfflineSessionId(null)
        warningShownRef.current = false
        toast.dismiss(WARNING_TOAST_ID)
        if (sessionId) trackEvent('Offline Session Ended', { offlineSessionId: sessionId, reason: 'restored' })
      }
    }
  }, [isOffline, isManualOffline, isTrulyOffline, offlineSessionStart, offlineSessionExpired, offlineSessionId, setOfflineSessionStart, setOfflineSessionId])

  // Effect 2 — polling interval to check remaining time and trigger expiry
  useEffect(() => {
    if (!isOffline || offlineSessionStart === null || offlineSessionExpired) return

    const tick = () => {
      const elapsed = Date.now() - offlineSessionStart
      const remaining = OFFLINE_SESSION.maxDurationMs - elapsed

      if (remaining <= 0) {
        ;(async () => {
          const currentSessionId = useAppStore.getState().offlineSessionId
          // One final connectivity check before locking the user out.
          // Hard 10s timeout prevents the user from stalling by manipulating response time.
          const timeout = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('timeout')), OFFLINE_SESSION.expiryCheckTimeoutMs)
          )
          try {
            await Promise.race([fetchCurrentUser(), timeout])
            // Still reachable — disable offline mode and inform the user
            setManualOffline(false)
            setOfflineSessionExpired(false)
            setOfflineSessionStart(null)
            setOfflineSessionId(null)
            warningShownRef.current = false
            if (currentSessionId) trackEvent('Offline Session Ended', { offlineSessionId: currentSessionId, reason: 'connectivity_restored' })
            toast.info(
                t('offlineSession.offlineDisabledMessage'),
                {
                  toastId: OFFLINE_DISABLED_TOAST_ID,
                  closeOnClick: true,
                }
            )

            return
          } catch {
            // Timed out or failed — fall through to lock out
          }

          // Confirmed unreachable — lock out and clear content bundle
          if (currentSessionId) trackEvent('Offline Session Expired', { offlineSessionId: currentSessionId })
          setOfflineSessionExpired(true)
          toast.dismiss(WARNING_TOAST_ID)
          try {
            await Promise.all([clearAllContentBundles(), clearMediaCache()])
          } catch (err) {
            console.error('[OfflineSession] cache clear failed:', err)
          }
        })()
        return
      }

      if (remaining <= OFFLINE_SESSION.warningThresholdMs && !warningShownRef.current) {
        warningShownRef.current = true
        toast.warning(
          t('offlineSession.warningMessage', { minutes: Math.ceil(remaining / 60_000) }),
          {
            toastId: WARNING_TOAST_ID,
            autoClose: false,
            closeOnClick: false,
          }
        )
      }
    }

    tick()
    const id = window.setInterval(tick, OFFLINE_SESSION.checkIntervalMs)
    return () => window.clearInterval(id)
  }, [isOffline, offlineSessionStart, offlineSessionExpired, setOfflineSessionExpired, setOfflineSessionStart, setOfflineSessionId, setManualOffline, t])

  // Effect 3 — attempt recovery when network returns while session is expired
  useEffect(() => {
    if (!offlineSessionExpired || isTrulyOffline) return
    if (recoveryInProgressRef.current) return

    recoveryInProgressRef.current = true
    ;(async () => {
      try {
        await fetchCurrentUser()
        const currentSessionId = useAppStore.getState().offlineSessionId
        setManualOffline(false)
        setOfflineSessionExpired(false)
        setOfflineSessionStart(null)
        setOfflineSessionId(null)
        warningShownRef.current = false
        if (currentSessionId) trackEvent('Offline Session Ended', { offlineSessionId: currentSessionId, reason: 'recovered_after_expiry' })
      } catch {
        // Server not reachable yet — apiClient interceptor will set isTrulyOffline(true),
        // which will re-run this effect when it flips back to false again
      } finally {
        recoveryInProgressRef.current = false
      }
    })()
  }, [isTrulyOffline, offlineSessionExpired, setManualOffline, setOfflineSessionExpired, setOfflineSessionStart, setOfflineSessionId])
}
